import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../components/ui/Toast';
import { reconcilePayment } from '../booking/bookingApi';
import { useReturnFromCheckout } from '../../lib/useReturnFromCheckout';
import { OUTSTANDING_KEY } from './outstanding';

/**
 * The shared "coach came back from checkout without hitting our success URL" handler for every
 * surface with a Pay button. The PWA sheet's Done button and bfcache Back restore the page with
 * its state intact, so the coach may have PAID and still landed here — check with Stripe rather
 * than re-offering payment until the webhook gets round to it (an unpaid session is a harmless
 * no). Clears the surface's spinner via `reset`, then refreshes both payment-facing queries.
 */
export function usePaymentReturn(payingId: number | null, reset: () => void) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useReturnFromCheckout(payingId !== null, () => {
    const returnedId = payingId;
    reset();
    void (async () => {
      if (returnedId !== null) {
        try {
          const { status } = await reconcilePayment(returnedId);
          if (status === 'confirmed') {
            toast('Payment received — your booking is confirmed', 'success');
          } else if (status === 'needs-resolution') {
            toast('Payment received — there is a problem with this booking and the club is resolving it', 'info');
          }
        } catch {
          // Can't tell — the refetch below still shows whatever the webhook has landed.
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] });
      await queryClient.invalidateQueries({ queryKey: OUTSTANDING_KEY });
    })();
  });
}
